/* helper.ts */

/* d3.js */
import * as d3 from "d3";

/* Utils */
import { getCohortGroupColor } from "src/utils/helper/charts/colorMapper";
import { capitalizeFirstLetter } from "src/utils/helper/string-transformation";
import {
  createChartTooltip,
  ScssProps,
} from "src/utils/helper/chart-tooltip";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Interfaces */
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";
import { BoxplotProps } from "src/shared/interfaces/charts/Boxplot";
import { ChartType } from "src/shared/constants/charts/Chart";

import {
  BoxplotTooltipProps,
  ChartTooltipRef,
} from "src/shared/interfaces/ChartTooltip";

export const createViewBox = (
  svg: SVGSelection,
  dimensions: ChartDimensionsProps
) => {
  const { height, width } = dimensions;
  return svg.attr("viewBox", `0 0 ${width} ${height}`);
};

const setTooltipData = (
  tooltipData: BoxplotTooltipProps
) => {
  if (!tooltipData) return [];

  return [
    {
      color: tooltipData.color,
      value: capitalizeFirstLetter(tooltipData.label),
    },
    {
      key: "Min",
      value: tooltipData.min,
    },
    {
      key: "Q1",
      value: tooltipData.q1,
    },
    {
      key: "Median",
      value: tooltipData.median,
    },
    {
      key: "Mean",
      value: tooltipData.mean,
    },
    {
      key: "Q3",
      value: tooltipData.q3,
    },
    {
      key: "Max",
      value: tooltipData.max,
    },
  ];
};

const setOutlierTooltipData = (
  tooltipData: BoxplotTooltipProps
) => {
  if (!tooltipData) return [];
  return [
    {
      key: "Outlier",
      value: tooltipData.outlier,
    },
  ];
};

export const drawVerticalBoxplots = (
  svg: SVGSelection,
  tooltipRef: React.RefObject<ChartTooltipRef>,
  g: d3.Selection<SVGGElement, unknown, null, undefined>,
  data: BoxplotProps[],
  xScale: d3.ScaleLinear<number, number>,
  yScale: d3.ScaleBand<string>
) => {
  const boxGroups = g
    .selectAll(".boxplot")
    .data(data)
    .enter()
    .append("g")
    .attr("class", "boxplot")
    .attr(
      "transform",
      (d) => `translate(0, ${yScale(d.name ?? "unknown")})`
    );

  boxGroups.each((d, index, nodes) => {
    const group = d3.select(nodes[index]);
    const { q1, q3, median, whiskerLow, whiskerHigh } =
      d.stats;
    const color = getCohortGroupColor(d.name, index);

    const gradientId = `gradient-${sanitizeGradientId(
      d.name ?? ""
    )}`;

    const boxPaddingFactor = 0;
    const boxY = yScale.bandwidth() * boxPaddingFactor;
    const boxHeight =
      yScale.bandwidth() * (1 - 2 * boxPaddingFactor);

    // Box
    group
      .selectAll(".box")
      .data([d])
      .enter()
      .append("rect")
      .attr("x", xScale(q1))
      .attr("width", xScale(q3) - xScale(q1))
      .attr("y", boxY)
      .attr("height", boxHeight)
      .attr("stroke", color)
      .attr("fill", `url(#${gradientId})`)
      .attr("fill-opacity", 0.75)
      .attr("rx", 4) // Rounded corners
      .attr("ry", 4)
      .on("mousemove", (event, d) => {
        const target = event.currentTarget as SVGElement;
        d3.select(target).attr("opacity", 0.85);

        const tooltipData: BoxplotTooltipProps = {
          label: d.name ?? "unknown",
          color: color,
          min: d.stats.min,
          q1: d.stats.q1,
          median: d.stats.median,
          mean: d.stats.mean,
          q3: d.stats.q3,
          max: d.stats.max,
        };

        const scssProps: ScssProps = {
          margin: -210,
        };

        createChartTooltip(
          tooltipRef,
          () => setTooltipData(tooltipData),
          svg,
          event,
          scssProps
        );
      })
      .on("mouseleave", (event, d) => {
        const target = event.currentTarget as SVGElement;
        d3.select(target).attr("opacity", 1);
        tooltipRef.current?.hideTooltip();
      });

    // Median
    group
      .append("line")
      .attr("x1", xScale(median))
      .attr("x2", xScale(median))
      .attr("y1", boxY)
      .attr("y2", boxY + boxHeight)
      .attr("stroke", color)
      .attr("stroke-width", 2);

    // Whisker lines
    group
      .append("line")
      .attr("x1", xScale(whiskerLow))
      .attr("x2", xScale(q1))
      .attr("y1", yScale.bandwidth() / 2)
      .attr("y2", yScale.bandwidth() / 2)
      .attr("stroke", color);

    group
      .append("line")
      .attr("x1", xScale(q3))
      .attr("x2", xScale(whiskerHigh))
      .attr("y1", yScale.bandwidth() / 2)
      .attr("y2", yScale.bandwidth() / 2)
      .attr("stroke", color);

    // Whisker caps
    group
      .append("line")
      .attr("x1", xScale(whiskerLow))
      .attr("x2", xScale(whiskerLow))
      .attr("y1", yScale.bandwidth() / 4)
      .attr(
        "y2",
        yScale.bandwidth() / 4 + yScale.bandwidth() / 2
      )
      .attr("stroke", color);

    group
      .append("line")
      .attr("x1", xScale(whiskerHigh))
      .attr("x2", xScale(whiskerHigh))
      .attr("y1", yScale.bandwidth() / 4)
      .attr(
        "y2",
        yScale.bandwidth() / 4 + yScale.bandwidth() / 2
      )
      .attr("stroke", color);

    // Outliers
    const outliers = d.y!.filter(
      (val) => val < whiskerLow || val > whiskerHigh
    );

    group
      .selectAll(".outlier")
      .data(outliers)
      .enter()
      .append("circle")
      .attr("class", "outlier")
      .attr("cx", (val) => xScale(val))
      .attr("cy", yScale.bandwidth() / 2)
      .attr("r", 3)
      .attr("fill", color)
      .on("mousemove", (event, val) => {
        const target = event.currentTarget as SVGElement;
        d3.select(target).attr("opacity", 0.85);

        const tooltipData: BoxplotTooltipProps = {
          label: d.name ?? "unknown",
          color: color,
          outlier: val,
        };

        const scssProps: ScssProps = {
          margin: -42,
        };

        createChartTooltip(
          tooltipRef,
          () => setOutlierTooltipData(tooltipData),
          svg,
          event,
          scssProps
        );
      })
      .on("mouseleave", (event) => {
        const target = event.currentTarget as SVGElement;
        d3.select(target).attr("opacity", 1);
        tooltipRef.current?.hideTooltip();
      });
  });
};

export const sanitizeGradientId = (label: string) =>
  label.replace(/[^a-zA-Z0-9-_]/g, "-");

export const createGradients = (
  svg: SVGSelection,
  subgroups: string[],
  type: ChartType
) => {
  const defs = svg.append("defs");

  subgroups.forEach((subgroup, index) => {
    const colorBase = getCohortGroupColor(subgroup, index);
    const gradientId = `gradient-${sanitizeGradientId(
      subgroup
    )}`;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    gradient
      .append("stop")
      .attr("offset", "0%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.brighter(0.3)?.toString() ??
          colorBase
      );

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr(
        "stop-color",
        d3.color(colorBase)?.darker(0.35)?.toString() ??
          colorBase
      );
  });
};
